import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import HeroSection from "./components/HeroSection";
import CaliforniaCommunitiesWeServe from "./components/CommunitiesWeServe";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import CitySEOHead from "@/components/feature/CitySEOHead";
import { faqs as caFaqs } from "./components/FAQSection";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

const californiaServices = [
  { name: "Emergency Passport Service", description: "Same-day and next-day emergency passport processing for California travelers with urgent international travel needs." },
  { name: "Expedited Passport Renewal", description: "2–3 week expedited passport renewal service for California residents." },
  { name: "New Passport Application", description: "First-time passport application assistance with document prep and acceptance facility scheduling for California residents." },
  { name: "Child Passport Service", description: "Minor passport applications with parental consent documentation for California families." },
  { name: "Lost or Stolen Passport Replacement", description: "Fast replacement of lost, stolen, or damaged passports for California residents." },
  { name: "Name Change Passport Update", description: "Passport name change service after marriage, divorce, or legal name change for California residents." },
  { name: "Second Passport", description: "Second valid passport applications for California frequent international travelers and business professionals." },
];

export default function CaliforniaPage() {
  return (
    <>
      <CitySEOHead
        city="California"
        state="California"
        stateAbbr="CA"
        route="/states/california"
        title="Expedited & Emergency Passport Services California | Same-Day CA Service | Fast Passport Center"
        description="Fast Passport Center provides emergency, expedited, and same-day passport services across all California cities. Serving Los Angeles, San Diego, San Francisco, Sacramento, and beyond. Regional agencies in LA, San Francisco, and San Diego."
        geoPlacename="California"
        geoPosition="36.7783;-119.4179"
        faqs={caFaqs.map((f) => ({ q: f.question, a: f.answer }))}
        servicesSchema={californiaServices}
        keywords="expedited passport California, emergency passport California, same day passport CA, fast passport Los Angeles, passport renewal San Diego, passport service San Francisco"
      />
      <div className="min-h-screen bg-white">
        <HeroSection />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
            </div>
          }
        >
          <CitiesDirectory />
        </Suspense>
        <CaliforniaCommunitiesWeServe />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
            </div>
          }
        >
          <StateResources />
        </Suspense>
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
            </div>
          }
        >
          <FAQSection />
        </Suspense>

        <section className="bg-[#F7F8FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                Passport Services
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
                How We Serve California
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                From the beaches of San Diego to the tech corridors of Silicon Valley, we help
                Californians get their passports fast. Three regional passport agencies in LA, San
                Francisco, and San Diego serve the state — but appointments are hard to get. We cut
                through the red tape.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-flight-takeoff-line",
                  title: "Emergency Same-Day Service",
                  desc: "For California travelers with flights within 72 hours. We hand-carry your application to a regional agency.",
                },
                {
                  icon: "ri-time-line",
                  title: "Expedited 2–3 Week Service",
                  desc: "Standard expedited processing with overnight shipping and tracking for California residents.",
                },
                {
                  icon: "ri-file-list-3-line",
                  title: "New Passport Applications",
                  desc: "First-time applicants in California — we handle document prep, photos, and acceptance facility scheduling.",
                },
                {
                  icon: "ri-refresh-line",
                  title: "Passport Renewals",
                  desc: "California renewals by mail or courier. We ensure your application is complete to avoid delays.",
                },
                {
                  icon: "ri-user-add-line",
                  title: "Child & Minor Passports",
                  desc: "California families with kids under 16 — we guide you through parental consent and documentation.",
                },
                {
                  icon: "ri-error-warning-line",
                  title: "Lost, Stolen & Damaged",
                  desc: "Fast replacement for California residents who need a new passport quickly.",
                },
              ].map((svc, i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                    <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading CTA...</div>
            </div>
          }
        >
          <FinalCTA />
        </Suspense>

        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <SEOCrossLinks state="CA" showCities={false} />
          </div>
        </section>
      </div>
    </>
  );
}